import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import type { DashboardPanel } from '@/types/Dashboards.types'

interface DashboardPanelsState {
  panels: DashboardPanel[]
  isLoading: boolean
  isBuilding: boolean
  error: string | null
  insightLoading: Record<string, boolean>
  insights: Record<string, string | null>
}

const initialState: DashboardPanelsState = {
  panels: [],
  isLoading: false,
  isBuilding: false,
  error: null,
  insightLoading: {},
  insights: {},
}

const dashboardPanelsSlice = createSlice({
  name: 'dashboardPanels',
  initialState,
  reducers: {
    setPanels(state, action: PayloadAction<DashboardPanel[]>) {
      state.panels = action.payload
      // Hydrate insights from persisted aiInsight on each panel.
      // insightLoading is false on load — loading state only activates on sync start.
      const newInsights: Record<string, string | null> = {}
      const newInsightLoading: Record<string, boolean> = {}
      for (const panel of action.payload) {
        newInsights[panel.id] = panel.aiInsight
        newInsightLoading[panel.id] = false
      }
      state.insights = newInsights
      state.insightLoading = newInsightLoading
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload
    },

    setIsBuilding(state, action: PayloadAction<boolean>) {
      state.isBuilding = action.payload
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },

    clearPanels(state) {
      state.panels = []
      state.error = null
      state.isLoading = false
      state.isBuilding = false
      state.insightLoading = {}
      state.insights = {}
    },

    setSyncInsightsLoading(state, action: PayloadAction<string[]>) {
      for (const panelId of action.payload) {
        state.insightLoading[panelId] = true
      }
    },

    clearSyncInsightsLoading(state, action: PayloadAction<string[]>) {
      for (const panelId of action.payload) {
        state.insightLoading[panelId] = false
      }
    },

    setPanelInsight(state, action: PayloadAction<{ panelId: string; insight: string }>) {
      state.insights[action.payload.panelId] = action.payload.insight
      state.insightLoading[action.payload.panelId] = false
    },
  },
})

export const dashboardPanelsReducer = dashboardPanelsSlice.reducer
export const dashboardPanelsActions = dashboardPanelsSlice.actions

/** Selector: true while the panel-build confirmation flow is active (shows the BuildingOverlay). */
export const selectDashboardPanelsIsBuilding = (state: { dashboardPanels: DashboardPanelsState }): boolean =>
  state.dashboardPanels.isBuilding

export const selectPanelInsight =
  (panelId: string) =>
  (state: { dashboardPanels: DashboardPanelsState }): string | null =>
    state.dashboardPanels.insights[panelId] ?? null

export const selectPanelInsightLoading =
  (panelId: string) =>
  (state: { dashboardPanels: DashboardPanelsState }): boolean =>
    state.dashboardPanels.insightLoading[panelId] ?? false

export const selectPanelIds =
  (state: { dashboardPanels: DashboardPanelsState }): string[] =>
    state.dashboardPanels.panels.map(p => p.id)

export default dashboardPanelsSlice
